import type { SignInIdentifierKind } from './sign-in-identifier-kind';

/**
 * Reads the one box on the sign-in form and says whether it holds an email address, a Kazakhstan
 * telephone number or an IIN.
 *
 * **A mirror of the server's `CustomerIdentifier`, and not the authority.** The server classifies
 * the value again and its answer is the one that decides; this exists so the customer is told what
 * they typed while they are still typing it, rather than after a round trip. That is worth the
 * duplication for the same reason the booking wizard's month grid is worth it: the alternative is a
 * form that says nothing until it has failed.
 *
 * The three shapes cannot be confused. Only an address contains `@`; the other two are digits, and
 * a Kazakhstan number is ten or eleven of them while an IIN is always twelve.
 */
export function classifyIdentifier(input: string): SignInIdentifierKind {
    const trimmed = input.trim();

    if (trimmed === '') {
        return 'unknown';
    }

    // By presence rather than by pattern: an address with a typo in it is still unmistakably an
    // attempt at an address, and calling it a malformed phone number would tell the customer to
    // correct the wrong thing.
    if (trimmed.includes('@')) {
        return 'email';
    }

    if (!/^[\d+\-() ]+$/.test(trimmed)) {
        return 'unknown';
    }

    const digits = trimmed.replace(/\D/g, '');

    // Before the phone, because twelve digits is only ever an IIN attempt — no Kazakhstan number
    // has that many — and a twelfth digit that fails the checksum is nothing rather than a number
    // of some other kind.
    if (digits.length === IIN_LENGTH) {
        return isValidIin(digits) ? 'iin' : 'unknown';
    }

    // Eleven with the trunk prefix or country code, ten written without either.
    if (digits.length === 11 && (digits.startsWith('8') || digits.startsWith('7'))) {
        return 'phone';
    }

    return digits.length === 10 ? 'phone' : 'unknown';
}

const IIN_LENGTH = 12;

const FIRST_WEIGHTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const SECOND_WEIGHTS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2];

/**
 * The same three checks the server makes, and the third is the one that matters: the check digit
 * catches a transposition, which is the mistake a person actually makes reading twelve digits off
 * an identity card.
 */
function isValidIin(digits: string): boolean {
    return hasValidDateOfBirth(digits) && hasValidCheckDigit(digits);
}

/**
 * The first six digits are `YYMMDD` and the seventh encodes the century and sex — 1 and 2 for the
 * 1800s, 3 and 4 for the 1900s, 5 and 6 for the 2000s. Without the century digit `990230` would be
 * ambiguous rather than wrong.
 */
function hasValidDateOfBirth(digits: string): boolean {
    const century = { '1': 1800, '2': 1800, '3': 1900, '4': 1900, '5': 2000, '6': 2000 }[digits[6]];

    if (century === undefined) {
        return false;
    }

    const year = century + Number(digits.slice(0, 2));
    const month = Number(digits.slice(2, 4));
    const day = Number(digits.slice(4, 6));

    if (month < 1 || month > 12) {
        return false;
    }

    return day >= 1 && day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * The twelfth digit is a weighted modulo-11 checksum of the first eleven. When the first pass lands
 * on 10 — which is not a digit — a second, rotated weight set is used, and a number that lands on
 * 10 twice has no valid check digit and does not exist.
 */
function hasValidCheckDigit(digits: string): boolean {
    let check = checksum(digits, FIRST_WEIGHTS);

    if (check === 10) {
        check = checksum(digits, SECOND_WEIGHTS);

        if (check === 10) {
            return false;
        }
    }

    return check === Number(digits[11]);
}

function checksum(digits: string, weights: number[]): number {
    return weights.reduce((total, weight, index) => total + Number(digits[index]) * weight, 0) % 11;
}
